import { memo, useState, useEffect, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { Home, Search, FileText, Shield, Wrench, Bell, ArrowRight, ArrowLeft, X, CheckCircle, Briefcase, BookOpen } from 'lucide-react'
import { useApp } from '../context/useApp'

const TOUR_VERSION = 'v1'

function tourKey(user) {
  return `heirloom_tour_${TOUR_VERSION}_${user._id}_${user.plan}`
}

const HOMEOWNER_STEPS = [
  {
    icon: Home,
    iconColor: 'text-gold',
    iconBg: 'bg-gold/15',
    tag: 'Welcome',
    title: 'Your Home Deserves a Permanent Record',
    body: 'Heirloom is your home\'s logbook — free forever. Track every repair, verify your ownership, and build a history that protects your investment and follows the property when you sell.',
  },
  {
    icon: Wrench,
    iconColor: 'text-blue-400',
    iconBg: 'bg-blue-400/15',
    tag: 'Home Log',
    title: 'Log Every Repair and Upgrade',
    body: 'Add maintenance entries in seconds. System, type, date, cost, contractor — all saved and timestamped. Your log builds automatically over time into the most complete picture of your home\'s history.',
  },
  {
    icon: Shield,
    iconColor: 'text-risk-green',
    iconBg: 'bg-risk-green/15',
    tag: 'Health Score',
    title: 'See Your Home\'s Health at a Glance',
    body: 'Heirloom scores each of your 9 home systems — HVAC, Roof, Plumbing, Electrical, and more — based on how recently they\'ve been serviced. Green is great. Amber means it\'s due. Red needs attention.',
  },
  {
    icon: Bell,
    iconColor: 'text-risk-amber',
    iconBg: 'bg-risk-amber/15',
    tag: 'Reminders',
    title: 'Never Miss Maintenance Again',
    body: 'Set recurring reminders for filters, HVAC service, gutter cleaning, and more. Upload inspection reports and Heirloom extracts the issues automatically — then tracks when you fix them.',
  },
  {
    icon: FileText,
    iconColor: 'text-purple-400',
    iconBg: 'bg-purple-400/15',
    tag: 'Selling',
    title: 'Your Log Becomes Your Disclosure',
    body: 'When you\'re ready to sell, pay once and generate a legally-structured seller disclosure built directly from your Heirloom log. No agent required. Buyers trust what\'s documented.',
  },
]

const BUYER_STEPS = [
  {
    icon: BookOpen,
    iconColor: 'text-gold',
    iconBg: 'bg-gold/15',
    tag: 'Welcome',
    title: 'See the Full History Before You Buy',
    body: 'A home inspection shows you one day. Heirloom shows you years. When a seller uses Heirloom, their entire maintenance log — every repair, every inspection — is available to you before you sign.',
  },
  {
    icon: Search,
    iconColor: 'text-blue-400',
    iconBg: 'bg-blue-400/15',
    tag: 'Search',
    title: 'Search Any Listed Property',
    body: 'Find properties on Heirloom by address. Active listings with a verified home log will appear. The more history the seller has logged, the more confident you can be in what you\'re buying.',
  },
  {
    icon: Shield,
    iconColor: 'text-risk-green',
    iconBg: 'bg-risk-green/15',
    tag: 'Reports',
    title: 'What You\'ll See in a Report',
    body: 'Purchase access to see a property\'s full maintenance log, system health scores across all 9 home systems, the ownership chain, and any generated seller disclosures — all in one place.',
  },
  {
    icon: Home,
    iconColor: 'text-gold',
    iconBg: 'bg-gold/15',
    tag: 'Own a Home?',
    title: 'Your Account Works Both Ways',
    body: 'If you own a home too, your account includes a free home log. Track your own maintenance, verify your ownership, and build the history that makes your home more valuable when it\'s your turn to sell.',
  },
]

const REALTOR_STEPS = [
  {
    icon: Briefcase,
    iconColor: 'text-purple-400',
    iconBg: 'bg-purple-400/15',
    tag: 'Welcome',
    title: 'Welcome to Your Realtor Dashboard',
    body: 'Heirloom gives you tools to close more deals, impress every buyer, and streamline disclosures. Your monthly credits cover risk reports and disclosure documents — all trackable from your dashboard.',
  },
  {
    icon: Search,
    iconColor: 'text-blue-400',
    iconBg: 'bg-blue-400/15',
    tag: 'Showing Tools',
    title: 'Share Report Links at Every Showing',
    body: 'Search any listed Heirloom property, copy a shareable link in one tap, or generate a printable QR code to display at the door. Buyers scan it and read the full verified home history right on their phone.',
  },
  {
    icon: FileText,
    iconColor: 'text-gold',
    iconBg: 'bg-gold/15',
    tag: 'Disclosures',
    title: 'Generate Disclosure Docs Instantly',
    body: 'For any listing with a Heirloom log, generate a print-ready seller disclosure document directly from the maintenance history. No manual data entry — it\'s built from what the owner actually logged.',
  },
  {
    icon: Shield,
    iconColor: 'text-risk-green',
    iconBg: 'bg-risk-green/15',
    tag: 'Credits',
    title: 'Your Monthly Credits',
    body: 'Your plan includes report and disclosure credits that reset each billing month. Track usage from your account dashboard. Upgrade or cancel anytime — no lock-in.',
  },
]

const CONTRACTOR_STEPS = [
  {
    icon: Briefcase,
    iconColor: 'text-emerald-400',
    iconBg: 'bg-emerald-400/15',
    tag: 'Welcome',
    title: 'Welcome to Your Contractor Business',
    body: 'Heirloom puts your business in front of homeowners actively maintaining their homes. Manage your profile, job portfolio, reviews, and seasonal campaigns — all from this dashboard.',
  },
  {
    icon: Search,
    iconColor: 'text-gold',
    iconBg: 'bg-gold/15',
    tag: 'Get Listed',
    title: 'Get Found in the Directory',
    body: 'Complete your profile, verify your email, and keep your plan active to appear in the Find a Pro directory. The checklist on your dashboard shows exactly what\'s left before you go live.',
  },
  {
    icon: Wrench,
    iconColor: 'text-blue-400',
    iconBg: 'bg-blue-400/15',
    tag: 'Jobs',
    title: 'Log Work, Invite Homeowners',
    body: 'Add past jobs to build your portfolio. When a job\'s address matches a Heirloom home, the owner is invited to confirm it — confirmed work shows as verified and lets them leave you a review.',
  },
  {
    icon: Bell,
    iconColor: 'text-risk-green',
    iconBg: 'bg-risk-green/15',
    tag: 'Grow',
    title: 'Reviews, Campaigns & Analytics',
    body: 'Earn homeowner-verified reviews (and reply to them), send seasonal maintenance campaigns to past customers, and track profile views, leads, and conversion on the Analytics tab.',
  },
]

function stepsForPlan(plan) {
  if (plan === 'homeowner') return HOMEOWNER_STEPS
  if (plan === 'buyer') return BUYER_STEPS
  if (plan === 'realtor' || plan === 'realtorpro') return REALTOR_STEPS
  if (plan === 'contractor') return CONTRACTOR_STEPS
  return []
}

export default memo(function GuidedTour() {
  const { user } = useApp()
  const [visible, setVisible] = useState(false)
  const [step, setStep] = useState(0)

  const openTour = useCallback(() => {
    setStep(0)
    setVisible(true)
  }, [])

  // Auto-show for new users
  useEffect(() => {
    if (!user) return
    const steps = stepsForPlan(user.plan)
    if (!steps.length) return
    if (!localStorage.getItem(tourKey(user))) {
      const t = setTimeout(openTour, 700)
      return () => clearTimeout(t)
    }
  }, [user?._id, user?.plan]) // eslint-disable-line react-hooks/exhaustive-deps

  // Listen for replay trigger from other pages
  useEffect(() => {
    const handler = () => openTour()
    window.addEventListener('heirloom:start-tour', handler)
    return () => window.removeEventListener('heirloom:start-tour', handler)
  }, [openTour])

  function handleDismiss() {
    localStorage.setItem(tourKey(user), '1')
    setVisible(false)
  }

  // Escape key to close
  useEffect(() => {
    if (!visible) return
    const handler = (e) => { if (e.key === 'Escape') handleDismiss() }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [visible]) // eslint-disable-line react-hooks/exhaustive-deps

  if (!user || !visible) return null

  const steps = stepsForPlan(user.plan)
  if (!steps.length) return null

  const current = steps[step]
  const isLast = step === steps.length - 1
  const Icon = current.icon

  function handleNext() {
    if (isLast) { handleDismiss(); return }
    setStep(s => s + 1)
  }

  function handlePrev() {
    setStep(s => Math.max(0, s - 1))
  }

  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
      style={{ background: 'rgba(8, 12, 20, 0.85)', backdropFilter: 'blur(6px)' }}
      onClick={(e) => { if (e.target === e.currentTarget) handleDismiss() }}
    >
      <div className="bg-navy-card border border-gold/25 rounded-2xl w-full max-w-md shadow-2xl overflow-hidden">

        {/* Gold top bar */}
        <div className="h-1 bg-gradient-to-r from-transparent via-gold to-transparent opacity-60" />

        <div className="p-8">
          {/* Header row */}
          <div className="flex items-center justify-between mb-6">
            {/* Progress dots */}
            <div className="flex items-center gap-1.5">
              {steps.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setStep(i)}
                  className={`rounded-full transition-all border-0 cursor-pointer p-0 ${
                    i === step
                      ? 'w-5 h-2 bg-gold'
                      : i < step
                      ? 'w-2 h-2 bg-gold/40'
                      : 'w-2 h-2 bg-navy-lighter'
                  }`}
                />
              ))}
            </div>
            <button
              onClick={handleDismiss}
              className="text-sepia/60 hover:text-parchment/70 bg-transparent border-0 cursor-pointer p-1 -mr-1 transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Icon */}
          <div className={`w-14 h-14 ${current.iconBg} rounded-2xl flex items-center justify-center mb-5`}>
            <Icon className={`w-7 h-7 ${current.iconColor}`} />
          </div>

          {/* Tag + Title */}
          <div className="mb-1">
            <span className="text-[10px] font-bold uppercase tracking-widest text-gold/70">{current.tag}</span>
          </div>
          <h2 className="font-serif text-2xl text-cream mb-3 leading-snug">{current.title}</h2>
          <p className="text-sm text-parchment/65 leading-relaxed mb-8">{current.body}</p>

          {/* Navigation */}
          <div className="flex items-center gap-3">
            {step > 0 && (
              <button
                onClick={handlePrev}
                className="flex items-center gap-1.5 px-4 py-2.5 bg-navy-lighter border border-slate-border rounded-xl text-sm text-parchment/60 hover:border-gold/30 hover:text-parchment/80 transition-all cursor-pointer border"
              >
                <ArrowLeft className="w-3.5 h-3.5" />
              </button>
            )}
            <button
              onClick={handleNext}
              className="flex-1 flex items-center justify-center gap-2 py-2.5 bg-gold hover:bg-gold-light text-white text-sm font-semibold rounded-xl transition-colors cursor-pointer border-0"
            >
              {isLast ? (
                <><CheckCircle className="w-4 h-4" /> Get Started</>
              ) : (
                <>Next <ArrowRight className="w-4 h-4" /></>
              )}
            </button>
          </div>

          {/* Skip link */}
          {!isLast && (
            <div className="text-center mt-4">
              <button
                onClick={handleDismiss}
                className="text-xs text-sepia/50 hover:text-sepia transition-colors bg-transparent border-0 cursor-pointer"
              >
                Skip tour
              </button>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  )
})
